import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { PlacesAddressField } from "@/components/PlacesAddressField";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ApiError } from "@/lib/apiClient";
import type { CurrentUser } from "@/lib/auth";
import { userIsBuero } from "@/lib/auth";
import {
  createAufmassunterlageAddress,
  createAufmassunterlageNote,
  createMontageunterlageAddress,
  createMontageunterlageNote,
  createProjectFotoSession,
  createWerkstattunterlageAddress,
  createWerkstattunterlageNote,
  getAufmassunterlageSharePointFiles,
  getMontageunterlageSharePointFiles,
  getProjectFotoMobileUploadQr,
  getWerkstattunterlageSharePointFiles,
  linkAufmassunterlageSharePointFiles,
  linkAufmassunterlagenFromProjectFotos,
  linkMontageunterlageSharePointFiles,
  linkMontageunterlagenFromProjectFotos,
  linkWerkstattunterlageSharePointFiles,
  linkWerkstattunterlagenFromProjectFotos,
  listProjectFotoFolders,
  listProjectFotos,
  openProjectFotoSession,
  resolveProjectFotoPreviewUrl,
  updateAufmassunterlage,
  updateMontageunterlage,
  updateWerkstattunterlage,
  uploadAufmassunterlageFile,
  uploadMontageunterlageFile,
  uploadProjectFotoFile,
  uploadWerkstattunterlageFile,
  type Montageunterlage,
  type MontageunterlageSharePointFile,
  type ProjectFoto,
  type ProjectFotoSession,
} from "@/lib/projectOverview";
import { StuecklisteEditorPanel } from "@/projectOverview/StuecklisteEditorPanel";
import type { DocumentPackKind } from "@/projectOverview/montageunterlagenTabs";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export type MontageAddMode =
  | "file"
  | "mobile"
  | "project_fotos"
  | "sharepoint"
  | "stueckliste"
  | "note"
  | "address";

const MONTAGE_FOTO_SESSION_TITLE = "Montageunterlagen";
const WERKSTATT_FOTO_SESSION_TITLE = "Werkstattunterlagen";
const AUFMASS_FOTO_SESSION_TITLE = "Aufmaßunterlagen";
const POLL_MS = 4000;

function buildMapsPreviewUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
}

function fmtSize(bytes?: number | null): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  return file.type.toLowerCase().startsWith("image/");
}

function dialogTitle(
  mode: MontageAddMode,
  editing: boolean,
  stuecklisteId?: number | null,
): string {
  if (editing) {
    return mode === "address" ? "Adresse bearbeiten" : "Notiz bearbeiten";
  }
  switch (mode) {
    case "file":
      return "Datei vom PC";
    case "mobile":
      return "Vom Handy hochladen";
    case "project_fotos":
      return "Projektfotos auswählen";
    case "sharepoint":
      return "Aus SharePoint";
    case "stueckliste":
      return stuecklisteId != null ? "Stückliste bearbeiten" : "Neue Stückliste";
    case "note":
      return "Notiz hinzufügen";
    case "address":
      return "Adresse hinzufügen";
    default:
      return "Hinzufügen";
  }
}

async function ensurePackFotoSession(
  orderId: number,
  pack: DocumentPackKind,
): Promise<ProjectFotoSession> {
  const sessionTitle =
    pack === "werkstatt"
      ? WERKSTATT_FOTO_SESSION_TITLE
      : pack === "aufmass"
        ? AUFMASS_FOTO_SESSION_TITLE
        : MONTAGE_FOTO_SESSION_TITLE;
  const folderNeedle =
    pack === "werkstatt" ? "werkstattunterlagen" : pack === "aufmass" ? "aufma" : "montageunterlagen";
  const [sessions, folders] = await Promise.all([
    listProjectFotos(orderId),
    listProjectFotoFolders(orderId).catch(() => []),
  ]);
  const titleMatch = (value: string | null | undefined) =>
    String(value || "").trim().toLowerCase() === sessionTitle.toLowerCase();

  const byTitle = sessions.find((s) => titleMatch(s.title));
  if (byTitle) return byTitle;

  const folder = folders.find(
    (f) =>
      titleMatch(f.title) ||
      String(f.folder_segment || "").toLowerCase().includes(folderNeedle),
  );
  if (folder?.sharepoint_folder_id && folder.folder_segment) {
    return openProjectFotoSession(orderId, {
      sharepoint_folder_id: folder.sharepoint_folder_id,
      folder_segment: folder.folder_segment,
      title: folder.title || sessionTitle,
    });
  }
  return createProjectFotoSession(orderId, sessionTitle);
}

export function MontageunterlagenAddDialog({
  open,
  mode,
  orderId,
  pack = "montage",
  user,
  groups,
  editingItem = null,
  initialStuecklisteId = null,
  autoStartCreate = false,
  onClose,
  onChanged,
  onStuecklisteEditingIdChange,
}: {
  open: boolean;
  mode: MontageAddMode | null;
  orderId: number;
  pack?: DocumentPackKind;
  user: CurrentUser | null;
  groups: string[];
  editingItem?: Montageunterlage | null;
  initialStuecklisteId?: number | null;
  autoStartCreate?: boolean;
  onClose: () => void;
  onChanged: () => void;
  onStuecklisteEditingIdChange?: (id: number | null) => void;
}) {
  const isBuero = userIsBuero(user);
  const isHandheld = useMediaQuery("(max-width: 1023px)");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [addressMapsUrl, setAddressMapsUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [spItems, setSpItems] = useState<MontageunterlageSharePointFile[]>([]);
  const [spHint, setSpHint] = useState("");
  const [spLoading, setSpLoading] = useState(false);
  const [selectedSp, setSelectedSp] = useState<Set<string>>(new Set());

  const [fotoSessions, setFotoSessions] = useState<ProjectFotoSession[]>([]);
  const [fotosLoading, setFotosLoading] = useState(false);
  const [selectedFotoIds, setSelectedFotoIds] = useState<Set<number>>(new Set());

  const [mobileSession, setMobileSession] = useState<ProjectFotoSession | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [mobileBooting, setMobileBooting] = useState(false);
  const linkedFotoIdsRef = useRef<Set<number>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activeMode = mode;
  const editing = editingItem != null && (activeMode === "note" || activeMode === "address");

  const mapsPreviewUrl = useMemo(() => {
    if (activeMode !== "address" && !editing) return null;
    if (addressMapsUrl) return addressMapsUrl;
    const text = bodyText.trim();
    return text ? buildMapsPreviewUrl(text) : null;
  }, [activeMode, editing, bodyText, addressMapsUrl]);

  const imageFotos = useMemo(() => {
    const out: Array<{ session: ProjectFotoSession; foto: ProjectFoto }> = [];
    for (const session of fotoSessions) {
      for (const foto of session.photos || []) {
        if (foto.is_image === false) continue;
        const ct = (foto.content_type || "").toLowerCase();
        if (ct && !ct.startsWith("image/")) continue;
        out.push({ session, foto });
      }
    }
    return out;
  }, [fotoSessions]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setSaving(false);
    setError(null);
    setTitle("");
    setBodyText("");
    setAddressMapsUrl(null);
    setSelectedFile(null);
    setSpItems([]);
    setSpHint("");
    setSelectedSp(new Set());
    setFotoSessions([]);
    setSelectedFotoIds(new Set());
    setMobileSession(null);
    setQrUri(null);
    setQrUrl(null);
    setMobileBooting(false);
    linkedFotoIdsRef.current = new Set();
    stopPoll();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [stopPoll]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (editingItem && (editingItem.entry_type === "note" || editingItem.entry_type === "address")) {
      setTitle(editingItem.title || "");
      setBodyText(editingItem.body_text || "");
      setAddressMapsUrl(editingItem.entry_type === "address" ? editingItem.maps_url || null : null);
    }
  }, [open, editingItem, resetState]);

  const loadSharepoint = useCallback(async () => {
    if (!isBuero) return;
    setSpLoading(true);
    setSpHint("Lade Projektdateien …");
    try {
      const payload =
        pack === "werkstatt"
          ? await getWerkstattunterlageSharePointFiles(orderId)
          : pack === "aufmass"
            ? await getAufmassunterlageSharePointFiles(orderId)
            : await getMontageunterlageSharePointFiles(orderId);
      const list = Array.isArray(payload?.items) ? payload.items : [];
      setSpItems(list);
      setSpHint(payload?.hint || (list.length ? `${list.length} Datei(en) im Projektordner.` : ""));
    } catch {
      setSpItems([]);
      setSpHint("Projektdateien konnten nicht geladen werden.");
    } finally {
      setSpLoading(false);
    }
  }, [isBuero, orderId, pack]);

  useEffect(() => {
    if (!open || activeMode !== "sharepoint" || !isBuero) return;
    void loadSharepoint();
  }, [open, activeMode, isBuero, loadSharepoint]);

  const loadProjectFotos = useCallback(async () => {
    setFotosLoading(true);
    setError(null);
    try {
      const list = await listProjectFotos(orderId);
      setFotoSessions(Array.isArray(list) ? list : []);
    } catch (err) {
      setFotoSessions([]);
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : "Projektfotos konnten nicht geladen werden.",
      );
    } finally {
      setFotosLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!open || activeMode !== "project_fotos") return;
    void loadProjectFotos();
  }, [open, activeMode, loadProjectFotos]);

  const syncNewFotosToMontage = useCallback(
    async (session: ProjectFotoSession) => {
      const photos = session.photos || [];
      const newIds = photos
        .map((p) => p.id)
        .filter((id) => !linkedFotoIdsRef.current.has(id));
      if (!newIds.length) return;
      try {
        if (pack === "werkstatt") {
          await linkWerkstattunterlagenFromProjectFotos(orderId, newIds);
        } else if (pack === "aufmass") {
          await linkAufmassunterlagenFromProjectFotos(orderId, newIds);
        } else {
          await linkMontageunterlagenFromProjectFotos(orderId, newIds);
        }
        for (const id of newIds) linkedFotoIdsRef.current.add(id);
        onChanged();
      } catch {
        /* next poll retries */
      }
    },
    [orderId, onChanged, pack],
  );

  useEffect(() => {
    if (!open || activeMode !== "mobile") return;
    let cancelled = false;
    setMobileBooting(true);
    setError(null);
    void (async () => {
      try {
        const session = await ensurePackFotoSession(orderId, pack);
        if (cancelled) return;
        for (const foto of session.photos || []) {
          linkedFotoIdsRef.current.add(foto.id);
        }
        const qr = await getProjectFotoMobileUploadQr(orderId, session.id);
        if (cancelled) return;
        setMobileSession(session);
        setQrUri(qr.qr_data_uri || null);
        setQrUrl(qr.url || null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError || err instanceof Error
              ? err.message
              : "QR-Upload konnte nicht gestartet werden.",
          );
        }
      } finally {
        if (!cancelled) setMobileBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, activeMode, orderId, pack]);

  useEffect(() => {
    if (!open || activeMode !== "mobile" || !mobileSession) {
      stopPoll();
      return;
    }
    stopPoll();
    pollRef.current = setInterval(() => {
      void listProjectFotos(orderId)
        .then((list) => {
          const updated = list.find((s) => s.id === mobileSession.id);
          if (!updated) return;
          setMobileSession(updated);
          void syncNewFotosToMontage(updated);
        })
        .catch(() => {
          /* ignore poll errors */
        });
    }, POLL_MS);
    return () => stopPoll();
  }, [open, activeMode, mobileSession?.id, orderId, stopPoll, syncNewFotosToMontage]);

  async function handleNoteAddressSubmit() {
    setSaving(true);
    setError(null);
    try {
      if (editingItem && (editingItem.entry_type === "note" || editingItem.entry_type === "address")) {
        const updateFn =
          pack === "werkstatt"
            ? updateWerkstattunterlage
            : pack === "aufmass"
              ? updateAufmassunterlage
              : updateMontageunterlage;
        await updateFn(orderId, editingItem.id, {
          title: title.trim() || undefined,
          body_text: bodyText.trim(),
          maps_url:
            editingItem.entry_type === "address"
              ? addressMapsUrl || mapsPreviewUrl || undefined
              : undefined,
        });
      } else if (activeMode === "note") {
        if (!bodyText.trim()) {
          setError("Bitte eine Notiz eingeben.");
          return;
        }
        const createNote =
          pack === "werkstatt"
            ? createWerkstattunterlageNote
            : pack === "aufmass"
              ? createAufmassunterlageNote
              : createMontageunterlageNote;
        await createNote(orderId, {
          body_text: bodyText.trim(),
          title: title.trim() || undefined,
        });
      } else if (activeMode === "address") {
        if (!bodyText.trim()) {
          setError("Bitte eine Adresse eingeben.");
          return;
        }
        const createAddress =
          pack === "werkstatt"
            ? createWerkstattunterlageAddress
            : pack === "aufmass"
              ? createAufmassunterlageAddress
              : createMontageunterlageAddress;
        await createAddress(orderId, {
          body_text: bodyText.trim(),
          title: title.trim() || undefined,
          maps_url: addressMapsUrl || mapsPreviewUrl || undefined,
        });
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleFileUpload() {
    if (!selectedFile) {
      setError("Bitte eine Datei auswählen.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isImageFile(selectedFile)) {
        const session = await ensurePackFotoSession(orderId, pack);
        const foto = await uploadProjectFotoFile(orderId, session.id, selectedFile);
        if (pack === "werkstatt") {
          await linkWerkstattunterlagenFromProjectFotos(orderId, [foto.id]);
        } else if (pack === "aufmass") {
          await linkAufmassunterlagenFromProjectFotos(orderId, [foto.id]);
        } else {
          await linkMontageunterlagenFromProjectFotos(orderId, [foto.id]);
        }
      } else if (pack === "werkstatt") {
        await uploadWerkstattunterlageFile(orderId, selectedFile, title.trim() || undefined);
      } else if (pack === "aufmass") {
        await uploadAufmassunterlageFile(orderId, selectedFile, title.trim() || undefined);
      } else {
        await uploadMontageunterlageFile(orderId, selectedFile, title.trim() || undefined);
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkSharepoint() {
    const ids = [...selectedSp];
    if (!ids.length) {
      setError("Bitte mindestens eine SharePoint-Datei auswählen.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (pack === "werkstatt") {
        await linkWerkstattunterlageSharePointFiles(orderId, ids);
      } else if (pack === "aufmass") {
        await linkAufmassunterlageSharePointFiles(orderId, ids);
      } else {
        await linkMontageunterlageSharePointFiles(orderId, ids);
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verknüpfen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkProjectFotos() {
    const ids = [...selectedFotoIds];
    if (!ids.length) {
      setError("Bitte mindestens ein Projektfoto auswählen.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (pack === "werkstatt") {
        await linkWerkstattunterlagenFromProjectFotos(orderId, ids);
      } else if (pack === "aufmass") {
        await linkAufmassunterlagenFromProjectFotos(orderId, ids);
      } else {
        await linkMontageunterlagenFromProjectFotos(orderId, ids);
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Übernahme fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (!activeMode) return null;

  const showFooter =
    activeMode === "note" ||
    activeMode === "address" ||
    activeMode === "file" ||
    activeMode === "sharepoint" ||
    activeMode === "project_fotos";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        activeMode === "file" && isHandheld && !editing
          ? "Aus Mediathek"
          : dialogTitle(activeMode, editing, initialStuecklisteId)
      }
      description={
        activeMode === "mobile"
          ? "QR-Code scannen – Fotos landen bei den Projektfotos und in den Montageunterlagen."
          : activeMode === "file"
            ? "Bilder werden auch bei den Projektfotos abgelegt."
            : undefined
      }
      size={activeMode === "stueckliste" || activeMode === "project_fotos" ? "xl" : "lg"}
      fullScreenMobile={activeMode === "stueckliste"}
      plainBackdrop
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      bodyClassName={
        activeMode === "stueckliste" ? "max-h-[min(80vh,860px)] overflow-y-auto" : undefined
      }
      footer={
        showFooter ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            {activeMode === "note" || activeMode === "address" ? (
              <Button type="button" loading={saving} onClick={() => void handleNoteAddressSubmit()}>
                {editing ? "Speichern" : "Hinzufügen"}
              </Button>
            ) : null}
            {activeMode === "file" ? (
              <Button type="button" loading={saving} onClick={() => void handleFileUpload()}>
                Hochladen
              </Button>
            ) : null}
            {activeMode === "sharepoint" ? (
              <Button
                type="button"
                loading={saving}
                disabled={selectedSp.size === 0}
                onClick={() => void handleLinkSharepoint()}
              >
                Ausgewählte übernehmen
              </Button>
            ) : null}
            {activeMode === "project_fotos" ? (
              <Button
                type="button"
                loading={saving}
                disabled={selectedFotoIds.size === 0}
                onClick={() => void handleLinkProjectFotos()}
              >
                {selectedFotoIds.size ? `${selectedFotoIds.size} übernehmen` : "Übernehmen"}
              </Button>
            ) : null}
          </div>
        ) : activeMode === "mobile" || activeMode === "stueckliste" ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        ) : null
      }
    >
      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {activeMode === "note" || activeMode === "address" ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-label-secondary">
              Bezeichnung (optional)
            </span>
            <input
              type="text"
              className="eh-input w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                activeMode === "address" ? "z. B. Baustelleneingang" : "z. B. Hinweis Montage"
              }
              maxLength={200}
            />
          </label>
          {activeMode === "note" ? (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-label-secondary">Notiz</span>
              <textarea
                className="eh-input min-h-[5rem] w-full resize-y"
                rows={4}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Hinweise für die Montage …"
                maxLength={8000}
              />
            </label>
          ) : (
            <PlacesAddressField
              value={bodyText}
              onChange={(address, mapsUrl) => {
                setBodyText(address);
                setAddressMapsUrl(mapsUrl ?? null);
              }}
            />
          )}
          {activeMode === "address" && mapsPreviewUrl ? (
            <a
              href={mapsPreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-sm text-accent hover:underline"
            >
              Vorschau in Google Maps
            </a>
          ) : null}
        </div>
      ) : null}

      {activeMode === "file" ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-label-secondary">
              Bezeichnung (optional)
            </span>
            <input
              type="text"
              className="eh-input w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Lageplan"
              maxLength={200}
            />
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setSelectedFile(file);
              if (file && !title.trim()) {
                setTitle(file.name.replace(/\.[^.]+$/, ""));
              }
            }}
          />
          <div
            className={clsx(
              "flex min-h-[6rem] cursor-pointer flex-col items-center justify-center rounded-ios border border-dashed border-separator px-4 py-4 text-center transition",
              "hover:border-accent hover:bg-accent/5",
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0] ?? null;
              setSelectedFile(file);
              if (file && !title.trim()) {
                setTitle(file.name.replace(/\.[^.]+$/, ""));
              }
            }}
          >
            {selectedFile ? (
              <>
                <p className="text-sm font-medium text-label">{selectedFile.name}</p>
                <p className="mt-1 text-xs text-label-tertiary">
                  {(selectedFile.size / 1024 / 1024).toFixed(1)} MB · Bild oder PDF
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-label lg:hidden">Tippen und Fotos aus der Mediathek wählen</p>
                <p className="hidden text-sm text-label lg:block">Datei hier ablegen oder auswählen</p>
                <p className="mt-1 text-xs text-label-tertiary">Bilder oder PDF, max. 20 MB</p>
              </>
            )}
          </div>
        </div>
      ) : null}

      {activeMode === "mobile" && isHandheld ? (
        <p className="py-6 text-center text-sm text-label-secondary">
          Auf dem Smartphone kannst du Fotos direkt aus der Mediathek wählen.
        </p>
      ) : null}

      {activeMode === "mobile" && !isHandheld ? (
        <div className="space-y-4">
          {mobileBooting ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-label-secondary">
              <Spinner />
              QR-Code wird vorbereitet …
            </div>
          ) : qrUri ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <ClickableQrCode
                src={qrUri}
                href={qrUrl}
                alt="Handy-Upload QR"
                className="h-52 w-52 rounded-ios border border-separator bg-white p-2 shadow-sm"
              />
              <p className="max-w-sm text-center text-sm text-label-secondary">
                Mit dem Handy scannen und Fotos hochladen. Neue Bilder erscheinen automatisch bei den
                Projektfotos und in den Montageunterlagen.
              </p>
              {mobileSession ? (
                <p className="text-xs text-label-tertiary">
                  {(mobileSession.photos || []).length} Datei(en) in diesem Ordner
                </p>
              ) : null}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-label-secondary">
              QR-Code konnte nicht geladen werden.
            </p>
          )}
        </div>
      ) : null}

      {activeMode === "sharepoint" ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-label-tertiary">{spHint || "Projektdateien auswählen."}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={spLoading}
              disabled={spLoading}
              onClick={() => void loadSharepoint()}
            >
              Neu laden
            </Button>
          </div>
          <ul className="max-h-72 space-y-1 overflow-y-auto text-sm">
            {spItems.map((it) => {
              const sid = String(it.id || "").trim();
              if (!sid) return null;
              const label = [
                it.name,
                fmtSize(it.size) ? ` · ${fmtSize(it.size)}` : "",
                it.already_linked ? " · bereits verknüpft" : "",
              ]
                .filter(Boolean)
                .join("");
              return (
                <li key={sid}>
                  <label
                    className={clsx(
                      "flex gap-2 rounded-md px-2 py-1",
                      it.already_linked
                        ? "cursor-default opacity-60"
                        : "cursor-pointer hover:bg-fill-tertiary",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      disabled={!!it.already_linked || saving}
                      checked={selectedSp.has(sid)}
                      onChange={(e) => {
                        setSelectedSp((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(sid);
                          else next.delete(sid);
                          return next;
                        });
                      }}
                    />
                    <span className="break-all">{label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {activeMode === "project_fotos" ? (
        <div>
          {fotosLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-label-secondary">
              <Spinner className="h-4 w-4" />
              Projektfotos werden geladen …
            </div>
          ) : imageFotos.length === 0 ? (
            <p className="py-6 text-sm text-label-secondary">
              Im Projekt sind noch keine Bilder hinterlegt.
            </p>
          ) : (
            <div className="max-h-[min(60vh,28rem)] space-y-4 overflow-y-auto">
              {fotoSessions.map((session) => {
                const photos = (session.photos || []).filter((foto) => {
                  if (foto.is_image === false) return false;
                  const ct = (foto.content_type || "").toLowerCase();
                  return !ct || ct.startsWith("image/");
                });
                if (!photos.length) return null;
                return (
                  <section key={session.id} className="space-y-2">
                    <h4 className="text-sm font-semibold text-label">
                      {session.title || "Projektfotos"}
                      <span className="ml-2 font-normal text-label-tertiary">
                        {photos.length} Bild{photos.length === 1 ? "" : "er"}
                      </span>
                    </h4>
                    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                      {photos.map((foto) => {
                        const checked = selectedFotoIds.has(foto.id);
                        const preview = resolveProjectFotoPreviewUrl(orderId, foto);
                        return (
                          <li key={foto.id}>
                            <label
                              className={
                                checked
                                  ? "block cursor-pointer overflow-hidden rounded-lg border-2 border-accent bg-card"
                                  : "block cursor-pointer overflow-hidden rounded-lg border border-separator bg-card hover:border-label-tertiary"
                              }
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={checked}
                                onChange={() => {
                                  setSelectedFotoIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(foto.id)) next.delete(foto.id);
                                    else next.add(foto.id);
                                    return next;
                                  });
                                }}
                              />
                              <img
                                src={preview}
                                alt={foto.filename}
                                crossOrigin="use-credentials"
                                className="h-24 w-full object-cover"
                              />
                              <span className="block truncate px-2 py-1.5 text-xs text-label-secondary">
                                {foto.original_filename || foto.filename}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {activeMode === "stueckliste" ? (
        <StuecklisteEditorPanel
          orderId={orderId}
          user={user}
          groups={groups}
          pack={pack === "werkstatt" ? "werkstatt" : "montage"}
          initialStuecklisteId={initialStuecklisteId}
          autoStartCreate={autoStartCreate}
          onChanged={onChanged}
          onEditingIdChange={onStuecklisteEditingIdChange}
        />
      ) : null}
    </Dialog>
  );
}
